<template>
    <div>
        <step-form-heading v-if="!props.reservedDateId" :icon="faCalendarStar">
            Reserved Date
            <template v-slot:description>Choose a date to reserve for Corporate only sending.</template>
        </step-form-heading>

        <reserved-date-details :reservedDateId="props.reservedDateId">
            <form-wrapper
                :sections="[
                    { id: 'when', label: 'When' },
                    { id: 'type', label: 'Type' },
                    { id: 'reason', label: 'Reason' },
                ]"
                :hideNav="!!props.reservedDateId"
            >
                <vx-expandable-card id="when">
                    <template v-slot:header>
                        <div class="flex items-center pb-3">
                            <span class="pe-4 me-4 border-y-0 border-s-0 border-e border-solid border-e-slate-400">
                                When
                            </span>
                            <span class="text-sm font-semibold text-slate-700 me-1">
                                {{ dateFormatted || '-' }}
                            </span>
                        </div>
                    </template>

                    <template v-slot:content>
                        <vx-date-time-picker
                            :modelValue="dateAsDate"
                            @update:modelValue="onDateChange"
                            :timezone="getClientTz()"
                            :minDate="dayjs().tz(getClientTz()).toDate()"
                            :maxDate="maxDate"
                            mode="date"
                            :error="displayFirstError(validation.date)"
                        />
                    </template>
                </vx-expandable-card>

                <vx-expandable-card id="type">
                    <template v-slot:header>Type</template>
                    <template v-slot:headerSubtitle> Which type should be reserved? </template>

                    <template v-slot:content>
                        <div class="flex flex-col gap-2" :aria-invalid="validation.type.$error ? 'true' : undefined">
                            <div
                                @click="reservedDate.marketing = !reservedDate.marketing"
                                data-test="marketing-type-card"
                                class="flex items-center py-4 ps-4 pe-5 border border-solid rounded-xl cursor-pointer"
                                :class="
                                    reservedDate.marketing ? 'bg-white border-sky-500' : 'bg-slate-50 border-slate-300'
                                "
                            >
                                <vx-checkbox :modelValue="reservedDate.marketing" />
                                <div class="ms-4">
                                    <div class="text-slate-800 text-sm font-semibold leading-4">Marketing</div>
                                    <ul
                                        class="list-disc list-outside ms-4 mt-1 text-slate-500 text-xs"
                                        v-html="audienceTypeDescription(AUDIENCE_TYPE_MARKETING)"
                                    ></ul>
                                </div>
                            </div>
                            <div
                                @click="reservedDate.transactional = !reservedDate.transactional"
                                data-test="transactional-type-card"
                                class="flex items-center py-4 ps-4 pe-5 border border-solid rounded-xl cursor-pointer"
                                :class="
                                    reservedDate.transactional
                                        ? 'bg-white border-sky-500'
                                        : 'bg-slate-50 border-slate-300'
                                "
                            >
                                <vx-checkbox :modelValue="reservedDate.transactional" />
                                <div class="ms-4">
                                    <div class="text-slate-800 text-sm font-semibold leading-4">Transactional</div>
                                    <ul
                                        class="list-disc list-outside ms-4 mt-1 text-slate-500 text-xs"
                                        v-html="audienceTypeDescription(AUDIENCE_TYPE_TRANSACTIONAL)"
                                    ></ul>
                                </div>
                            </div>
                        </div>
                        <p v-if="displayFirstError(validation.type)" class="mt-2 px-0.5 text-xs text-rose-800">
                            {{ displayFirstError(validation.type) }}
                        </p>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card id="reason">
                    <template v-slot:header>Reason</template>

                    <template v-slot:content>
                        <vx-input
                            v-model="reservedDate.reason"
                            size="lg"
                            :rows="4"
                            :maxlength="2000"
                            :error="displayFirstError(validation.reason)"
                        />
                    </template>
                </vx-expandable-card>

                <vx-sticky-footer>
                    <vx-button href="/reserved-dates" class="w-20" size="lg" color="muted"> Cancel </vx-button>
                    <vx-button
                        @click="submit()"
                        data-test="reserved-date-save"
                        class="grow"
                        size="lg"
                        color="primary"
                        :loading="processing"
                    >
                        {{ props.reservedDateId ? 'Update' : 'Reserve Date' }}
                    </vx-button>
                </vx-sticky-footer>
            </form-wrapper>
        </reserved-date-details>
    </div>
</template>

<script lang="ts" setup>
import { faCalendarStar, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import ReservedDateDetails from '~/components/reserved-dates/ReservedDateDetails.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import {
    VxButton,
    VxCheckbox,
    VxDateTimePicker,
    VxExpandableCard,
    VxInput,
    VxStickyFooter,
} from '@voxie/frontend-components';
import { defaultReservedDate, useReservedDateDetails } from '~/components/reserved-dates/useReservedDateDetails';
import { displayFirstError } from '~/utils/validation';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { required, maxLength, helpers } from '@vuelidate/validators';
import { computed } from 'vue';
import dayjs from '~/utils/dayjs';
import { getClientTz } from '~/utils/date';
import { audienceTypeDescription, AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL } from '~/constants/campaign';

const props = defineProps<{
    reservedDateId?: number;
}>();

const { reservedDate, save, onMountedReservedDate } = useReservedDateDetails();

const maxDate = computed((): Date => {
    return dayjs().tz(getClientTz()).add(4, 'month').endOf('month').toDate();
});

const dateAsDate = computed((): Date | undefined => {
    if ('' === reservedDate.value.date) {
        return undefined;
    }
    const parsed = dayjs.tz(reservedDate.value.date, getClientTz());
    return parsed.isValid() ? parsed.toDate() : undefined;
});

const dateFormatted = computed(() => {
    if ('' === reservedDate.value.date) {
        return '';
    }
    const parsed = dayjs.tz(reservedDate.value.date, getClientTz());
    return parsed.isValid() ? parsed.format('MMM D, YYYY') : '';
});

const onDateChange = (date?: Date) => {
    reservedDate.value.date = date ? dayjs(date).tz(getClientTz()).format('YYYY-MM-DD') : '';
};

const atLeastOneType = helpers.withMessage(
    'At least one audience type must be selected.',
    () => reservedDate.value.marketing || reservedDate.value.transactional
);

const { validation, submit, processing } = useFormSubmission({
    data: reservedDate,
    validation: {
        date: { required },
        type: { atLeastOneType },
        reason: { maxLength: maxLength(2000) },
    },
    submit: async () => {
        return await save();
    },
    reset: () => {
        if (!props.reservedDateId) {
            reservedDate.value = defaultReservedDate();
        }
    },
    preserveScroll: () => Boolean(props.reservedDateId),
    notification: () => ({
        message: `Reserved date ${props.reservedDateId ? 'updated' : 'created'}`,
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to Reserved Dates',
            href: '/reserved-dates',
        },
    }),
});

onMountedReservedDate(() => {
    if (props.reservedDateId) {
        validation.value.$reset();
    }
});
</script>
